import { useEffect, useState } from 'react'
import {
  adminApi, ApiError,
  type AdminOrganization, type ConnectionStatus,
} from '../lib/api'
import { testBrowserKey } from '../lib/basemap'

type Field = {
  key: string
  label: string
  hint?: string
  secret?: boolean
  /** Pick from the providers the server says exist for this channel, rather
   *  than typing one. A free-text box holding the name of the only adapter
   *  that exists is a trap: the grey hint reads as a value, it saves empty,
   *  and the only symptom is a send failing later.
   *
   *  `levels` is the Map card's level (migration 056), from the server's own
   *  list -- the values the database accepts and the sentence beside each are
   *  one definition, `geocoding.describe_levels`. */
  choices?: 'providers' | 'levels'
  /** Drawn inside the collapsed "own Google keys" section rather than in the
   *  card's main list. The platform's keys are the normal case, so a pair of
   *  empty key boxes on every Map card would read as "not set up". */
  group?: 'own_google_keys'
  /** A stored secret that can be FORGOTTEN, not just replaced. Blank means
   *  "keep what is stored", so going back to the platform's key needs a verb
   *  of its own. */
  clearable?: boolean
  /** A Google browser key, which only a browser can test: see
   *  `basemap.testBrowserKey`. Tested as TYPED -- a stored key never comes
   *  back to this page. */
  browserTest?: boolean
}

/** One map level as the server describes it. */
type MapLevel = {
  value: string
  label: string
  note: string
  terms: string | null
}

const WHOSE: Record<string, string> = {
  own: 'this customer’s own',
  platform: 'the platform’s',
}

/** One card per connection, each with a test button.
 *
 *  Know the customer is wired correctly before handing over the login, rather
 *  than finding out when their first real call fails to go out. */
export const CONNECTIONS: {
  kind: string
  tab: string
  title: string
  blurb: string
  fields: Field[]
}[] = [
  {
    kind: 'dispatch',
    tab: 'Dispatch',
    title: 'Dispatch panel',
    blurb:
      'Provisions dispatchers and mints the sign-on token. One integration per customer, all sharing one origin — so onboarding never needs a deploy on their side.',
    fields: [
      { key: 'base_url', label: 'Base URL' },
      { key: 'integration_id', label: 'Integration ID', hint: 'crm_1a2b3c4d' },
      { key: 'secret', label: 'API key', secret: true, hint: 'crm_1a2b3c4d.<secret>' },
      {
        key: 'signing_key',
        label: 'Ed25519 signing key (PEM)',
        secret: true,
        hint: 'Printed once by crm:register and never recoverable.',
      },
    ],
  },
  {
    kind: 'pbx',
    tab: 'Phone',
    title: 'VitalPBX',
    blurb:
      'One authenticated AMI connection, listening to the call event stream. Every ring arrives as an event carrying the caller ID. The tenant prefix is required: an AMI login on a shared PBX sees every tenant on it, and without the prefix this customer would receive everybody else\u2019s callers.',
    fields: [
      { key: 'host', label: 'Host' },
      { key: 'port', label: 'Port', hint: 'Default 5038' },
      { key: 'ami_username', label: 'AMI username' },
      { key: 'secret', label: 'AMI secret', secret: true },
      // Required. An AMI login on a shared VitalPBX sees every tenant on the
      // machine, so without this the CRM would take in other customers'
      // callers by name.
      { key: 'tenant_prefix', label: 'Tenant prefix — REQUIRED',
        hint: 'T46 — the listener refuses to run without it' },
    ],
  },
  {
    kind: 'email',
    tab: 'Email',
    title: 'Email — SMTP2GO',
    blurb:
      'Per-organization credentials, so one customer’s bounce rate cannot damage another’s deliverability. Test sends a real message to the sender address.',
    fields: [
      { key: 'provider', label: 'Provider', choices: 'providers' },
      { key: 'from_address', label: 'From address — the test message goes here too' },
      { key: 'secret', label: 'API key', secret: true },
    ],
  },
  {
    kind: 'sms',
    tab: 'SMS',
    title: 'SMS',
    blurb:
      'Through the customer’s own text server. Test proves the key and the sending number without sending anything — a text costs money and lands on a phone, and the only address a test could use is the organization’s own number. The signing secret is separate: it verifies webhooks coming back the other way.',
    fields: [
      { key: 'provider', label: 'Provider', choices: 'providers' },
      { key: 'from_address', label: 'Sending number' },
      // Per customer, like every other integration's address. Empty means the
      // adapter's own default, which is what every customer on the shared text
      // server wants -- it exists so one on a different instance is a field
      // rather than a deploy.
      {
        key: 'base_url',
        label: 'API endpoint — leave empty unless this customer is on a different server',
        hint: 'https://text.advancetalk.ca/api/v1',
      },
      { key: 'secret', label: 'API key', secret: true },
      // A SECOND credential, in the opposite direction. The API key above
      // authenticates us to them; this verifies them to us. Storing one in the
      // other's box means either texts stop sending or every webhook is
      // refused — and both look like the other problem.
      {
        key: 'signing_key',
        label: 'Webhook signing secret',
        secret: true,
        hint: 'whsec_… — shown once when the endpoint is created. Verifies delivery receipts and inbound texts posted to /api/webhooks/text.',
      },
    ],
  },
  {
    kind: 'traccar',
    tab: 'Traccar',
    title: 'Traccar',
    blurb:
      'Each organization gets its own Traccar account, so Traccar itself refuses to return another customer’s fleet. Two locks instead of one.',
    fields: [
      { key: 'base_url', label: 'Base URL' },
      { key: 'username', label: 'Username' },
      { key: 'secret', label: 'Password', secret: true },
    ],
  },
  {
    kind: 'geocoder',
    tab: 'Map',
    title: 'Map / geocoding',
    blurb:
      'Turns an address into a point, so a dispatcher can see it land in the right borough before people are sent to it, and suggests addresses while he types. The LEVEL decides who is asked. A: the OpenStreetMap-side provider below does everything. A + Google search boost: Google suggests while he types, and the provider below places what he picks. B: Google suggests, places every address and draws every map on screen. Google levels spend the platform’s Google keys (Platform settings, top of the console) unless this customer has his own, below. Switching this ON starts sending caller addresses to those services — to Photon, a search on this same server, they never leave it. Test checks what the level uses and writes no coordinate anywhere.',
    fields: [
      { key: 'map_level', label: 'Map level', choices: 'levels' },
      { key: 'provider', label: 'OpenStreetMap-side provider', choices: 'providers' },
      {
        key: 'base_url',
        label: 'Base URL',
        hint: 'https://nominatim.openstreetmap.org — change it to a self-hosted or paid Nominatim and nothing else moves. Photon: http://photon:2322 — set it in the same save that switches the provider, or the OpenStreetMap address is kept and Photon refuses it.',
      },
      {
        key: 'contact',
        label: 'Contact address',
        hint: 'Required by the free OpenStreetMap server, which blocks callers that do not identify themselves. Photon and Google do not use it. Not a credential.',
      },
      {
        key: 'tile_url',
        label: 'Tile URL',
        hint: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png — a separate service from geocoding, failing separately. Keep it filled at Level B too: it is what a map falls back to when Google does not load.',
      },
      {
        key: 'tile_attribution',
        label: 'Tile attribution',
        hint: 'Shown under every map. A condition of using OpenStreetMap’s tiles.',
      },
      {
        key: 'bias_latitude',
        label: 'Service-area latitude',
        hint: '45.5019 — results near here are preferred, never restricted.',
      },
      { key: 'bias_longitude', label: 'Service-area longitude', hint: '-73.5674' },
      {
        key: 'bias_radius_metres',
        label: 'Service-area radius (m)',
        hint: '25000. A preference and not a fence: a real call just outside it still resolves.',
      },
      // `secret`, not `api_key`. Every card's secret is called `secret` on the
      // wire; this one once said `api_key`, the server ignored it, and the key
      // was silently never stored. Since 056 it is this customer's OWN Google
      // server key, used instead of the platform's.
      {
        key: 'secret',
        label: 'This customer’s own Google server key',
        secret: true,
        clearable: true,
        group: 'own_google_keys',
        hint: 'blank uses the platform’s',
      },
      {
        key: 'browser_key',
        label: 'This customer’s own Google browser key',
        secret: true,
        clearable: true,
        browserTest: true,
        group: 'own_google_keys',
        hint: 'blank uses the platform’s',
      },
    ],
  },
]

export function Connections({
  org,
  only,
}: {
  org: AdminOrganization
  only?: string
}) {
  const [state, setState] = useState<Record<string, any> | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({})
  const [tests, setTests] = useState<Record<string, { ok: boolean; detail: string }>>({})
  const [error, setError] = useState<string | null>(null)
  // Which card's webhook URL was just copied, so the button can say so.
  const [copied, setCopied] = useState<string | null>(null)
  // Which stored secrets the operator has asked to type over. A stored secret
  // is shown as filled and locked, because an empty box beside a working
  // connection reads as "not configured" — which is exactly the wrong thing to
  // believe about a customer who is live.
  const [replacing, setReplacing] = useState<Record<string, boolean>>({})
  // A browser key tried in this browser, per `kind.field`, as it was typed.
  const [keyTests, setKeyTests] =
    useState<Record<string, { ok: boolean; detail: string } | undefined>>({})

  async function load() {
    try {
      setState(await adminApi.connections(org.id))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load connections.')
    }
  }

  useEffect(() => {
    load()
    setDrafts({})
    setTests({})
    setReplacing({})
    setKeyTests({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id])

  /** Forget one stored key -- on the Map card, this customer's own Google key,
   *  which puts him back on the platform's. Saved at once and on its own,
   *  because it is a decision about money rather than an edit to a form. */
  async function clearKey(kind: string, field: Field) {
    if (
      !confirm(
        `Clear “${field.label}”?\n\nThe stored key is forgotten. ` +
        'If this customer is on a Google level, his requests use the ' +
        'platform’s key from now on — or none, if Platform settings has none.',
      )
    ) return
    try {
      setState(await adminApi.connectionSave(org.id, kind, { clear: [field.key] }))
      const draft = drafts[kind] ?? {}
      setDrafts({ ...drafts, [kind]: { ...draft, [field.key]: '' } })
      setReplacing({ ...replacing, [`${kind}.${field.key}`]: false })
      setKeyTests({ ...keyTests, [`${kind}.${field.key}`]: undefined })
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not clear that key.')
    }
  }

  async function tryBrowserKey(kind: string, field: Field, key: string) {
    const at = `${kind}.${field.key}`
    setKeyTests({ ...keyTests, [at]: { ok: false, detail: 'Testing in this browser…' } })
    const result = await testBrowserKey(key.trim())
    setKeyTests((now) => ({ ...now, [at]: result }))
  }

  async function save(kind: string) {
    const body: Record<string, unknown> = { ...(drafts[kind] ?? {}) }
    if (body.port) body.port = Number(body.port)
    // A blank secret field means "leave the stored one alone", never "clear it".
    for (const k of Object.keys(body)) if (body[k] === '') delete body[k]
    try {
      setState(await adminApi.connectionSave(org.id, kind, body))
      setDrafts({ ...drafts, [kind]: {} })
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save.')
    }
  }

  async function test(kind: string) {
    setTests({ ...tests, [kind]: { ok: false, detail: 'Testing…' } })
    try {
      setTests({ ...tests, [kind]: await adminApi.connectionTest(org.id, kind) })
      await load()
    } catch (e) {
      setTests({
        ...tests,
        [kind]: { ok: false, detail: e instanceof ApiError ? e.message : 'Test failed.' },
      })
    }
  }

  async function revoke(kind: string, title: string) {
    const seats = state?.seats
    const extra =
      kind === 'dispatch' && seats?.provisioned
        ? `\n\n${seats.provisioned} provisioned seat(s) will be unlinked from that ` +
          'server. The seats, who is assigned to them, and the hold log all stay.'
        : ''
    if (
      !confirm(
        `Disconnect ${title}?\n\nThe stored credentials are cleared and the ` +
        `connection is switched off. This CRM will stop contacting that ` +
        `server entirely.${extra}`,
      )
    ) return
    try {
      setState(await adminApi.connectionRevoke(org.id, kind))
      setDrafts({ ...drafts, [kind]: {} })
      setTests({ ...tests, [kind]: undefined as never })
      setReplacing({})
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not disconnect.')
    }
  }

  if (!state) return null


  return (
    <div className="pad">
      {error && <div className="notice">{error}</div>}

      {CONNECTIONS.filter((c) => !only || c.kind === only).map((c) => {
        const saved = state[c.kind] ?? {}
        const draft = drafts[c.kind] ?? {}
        const result = tests[c.kind]

        const status: ConnectionStatus | undefined = saved.status

        // One origin per customer, and the webhook's tenant is read from the
        // hostname rather than from anything in the payload — so this string
        // is not cosmetic, it is what keeps two organizations' receipts apart.
        const webhookUrl =
          `https://${org.slug}.crm.advancetalk.ca/api/webhooks/text`

        const renderField = (f: Field) => {
          const isSet =
            (f.key === 'secret' && (saved.has_secret || saved.has_api_key)) ||
            (f.key === 'browser_key' && saved.has_browser_key) ||
            (f.key === 'signing_key' &&
             // Two rows, two column names, one field key. The SMS
             // card is a channel_configs row and reports
             // `has_signing_secret`; the dispatch card is a
             // dispatch_configs row and reports `has_signing_key`.
             // Checking only the latter made a stored webhook
             // secret render as an empty field -- which reads as
             // "never saved" for the one credential whose absence
             // silently refuses every webhook.
             (saved.has_signing_key || saved.has_signing_secret)) ||
            (f.key === 'api_key' && saved.has_api_key)

          // A stored secret is shown as filled and locked. It is never sent
          // back to the browser — those are bullets, not the value — but an
          // empty box next to a live connection is read as "nothing is set
          // here", and that is the misreading worth designing out.
          const locked = f.secret && isSet && !replacing[`${c.kind}.${f.key}`]

          // THE LEVEL IS RADIO BUTTONS, NOT A SELECT, so the sentence
          // beside each choice -- above all the boost's line about
          // Google's terms -- is on screen at the moment of choosing
          // rather than hidden inside a closed list. Not a `.field`:
          // `.field input` is full-width and would stretch a radio.
          if (f.choices === 'levels') {
            const levels: MapLevel[] = state.levels ?? []
            if (levels.length === 0) return null
            const current = draft[f.key] ?? saved[f.key] ?? 'osm'
            return (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <div className="field" style={{ marginBottom: 6 }}>
                  <label>{f.label}</label>
                </div>
                {levels.map((level) => (
                  <div key={level.value} style={{ marginBottom: 10 }}>
                    <label className="check">
                      <input
                        type="radio"
                        name={`${c.kind}-${f.key}`}
                        value={level.value}
                        checked={current === level.value}
                        onChange={() =>
                          setDrafts({
                            ...drafts,
                            [c.kind]: { ...draft, [f.key]: level.value },
                          })
                        }
                      />
                      {level.label}
                    </label>
                    <div className="dim"
                         style={{ margin: '2px 0 0 22px', maxWidth: '62ch', lineHeight: 1.5 }}>
                      {level.note}
                      {level.terms && (
                        <>
                          {' '}
                          <b style={{ color: 'var(--wait)' }}>{level.terms}</b>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          }

          // The providers the server says can actually carry this channel.
          const choices: string[] | null =
            f.choices === 'providers' ? (state.providers?.[c.kind] ?? []) : null

          if (choices) {
            const current = draft[f.key] ?? saved[f.key] ?? ''
            return (
              <div className="field" key={f.key}>
                <label>{f.label}</label>
                {choices.length === 0 ? (
                  <div className="dim" style={{ padding: '9px 0' }}>
                    None is built for this channel yet. The seam is there —
                    adding one is a file, not a rewrite — but nothing can
                    send until a vendor is chosen.
                  </div>
                ) : (
                  <select
                    className="orgpick wide"
                    value={current}
                    onChange={(e) =>
                      setDrafts({
                        ...drafts,
                        [c.kind]: { ...draft, [f.key]: e.target.value },
                      })
                    }
                  >
                    {/* No blank option once a provider exists. Leaving it
                        unset is not a state anybody wants, and it was
                        reachable only by mistake. */}
                    {!current && <option value="">Choose one…</option>}
                    {choices.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                )}
              </div>
            )
          }

          return (
            <div className="field" key={f.key}>
              <label>
                {f.label}
                {f.secret && isSet && <span className="isset"> — stored</span>}
              </label>
              <div className="fieldrow">
                <input
                  type={f.secret ? 'password' : 'text'}
                  value={
                    locked
                      ? '••••••••••••••••'
                      : (draft[f.key] ?? (f.secret ? '' : (saved[f.key] ?? '')))
                  }
                  readOnly={locked}
                  placeholder={f.secret && isSet ? 'leave blank to keep' : f.hint}
                  onChange={(e) =>
                    setDrafts({
                      ...drafts,
                      [c.kind]: { ...draft, [f.key]: e.target.value },
                    })
                  }
                />
                {f.secret && isSet && (
                  <button
                    className="ghost tiny"
                    type="button"
                    onClick={() => {
                      const key = `${c.kind}.${f.key}`
                      setReplacing({ ...replacing, [key]: !replacing[key] })
                      setDrafts({ ...drafts, [c.kind]: { ...draft, [f.key]: '' } })
                    }}
                  >
                    {locked ? 'Replace' : 'Keep stored'}
                  </button>
                )}
                {f.clearable && isSet && locked && (
                  <button className="ghost tiny danger" type="button"
                          onClick={() => clearKey(c.kind, f)}>
                    Clear
                  </button>
                )}
                {/* ONLY A TYPED KEY CAN BE TRIED. A stored one never
                    comes back to this page, and a Google browser key is
                    refused anywhere but a page on this platform -- so it
                    is tried here, in this browser, before it is saved. */}
                {f.browserTest && !locked && (draft[f.key] ?? '').trim() && (
                  <button className="ghost tiny" type="button"
                          onClick={() => tryBrowserKey(c.kind, f, draft[f.key] ?? '')}>
                    Test in this browser
                  </button>
                )}
              </div>
              {keyTests[`${c.kind}.${f.key}`] && (
                <div className={`testresult ${keyTests[`${c.kind}.${f.key}`]!.ok ? 'ok' : 'bad'}`}
                     style={{ marginTop: 5 }}>
                  {keyTests[`${c.kind}.${f.key}`]!.detail}
                </div>
              )}
            </div>
          )
        }

        const own = c.fields.filter((f) => f.group === 'own_google_keys')
        const platform = state.platform_google ?? {}

        return (
          <div key={c.kind}>
            <h2>
              {c.title}
              <StatusPill status={status} />
            </h2>
            <p className="blurb">{c.blurb}</p>

            <StatusLine kind={c.kind} status={status} saved={saved}
                        seats={state.seats} listening={state.listening} />

            {/* THE URL COMES FIRST, ALWAYS.

                It used to be shown only once a signing secret was stored,
                which is backwards: the secret is what the text server hands
                you AFTER you register this URL with it. On a customer with
                nothing configured — the exact moment you need it — the screen
                showed no URL at all.

                It is also per organization. The webhook's tenant is decided
                by the HOSTNAME it arrives on, so each customer needs its own
                endpoint registered under its own subdomain with its own
                secret. Registering one customer's URL for another files their
                receipts against the wrong organization. */}
            {c.kind === 'sms' && (
              <div className="webhookurl">
                <div>
                  <b>Register this URL</b> on the text server, under this
                  customer’s own tenant → Webhooks. It gives you a{' '}
                  <code>whsec_…</code> once — paste that into “Webhook signing
                  secret” below.
                  <br />
                  <code>{webhookUrl}</code>
                </div>
                <button
                  className="ghost compact"
                  onClick={() => {
                    // Clipboard access is refused outside a secure context and
                    // can be denied outright; a URL you cannot copy must still
                    // be a URL you can read, and it is, right above.
                    navigator.clipboard?.writeText(webhookUrl)
                      .then(() => setCopied(c.kind))
                      .catch(() => setCopied(null))
                  }}
                >
                  {copied === c.kind ? 'Copied' : 'Copy'}
                </button>
              </div>
            )}

            {/* Webhooks fail in the opposite direction from everything else on
                this screen: they arrive, so Test cannot prove them and silence
                is ambiguous. A recent refusal means the signing secret is
                wrong; silence in both means nothing is reaching us at all,
                which is a URL or a firewall rather than a credential. */}
            {c.kind === 'sms' && (
              <p className="blurb">
                {!saved.has_signing_secret ? (
                  <>No webhook signing secret is stored, so every webhook the
                    text server sends is being refused. Until one is saved
                    here, delivery receipts and inbound texts do not arrive.</>
                ) : saved.last_webhook_refused_at &&
                 (!saved.last_webhook_at ||
                  saved.last_webhook_refused_at > saved.last_webhook_at) ? (
                  <b style={{ color: 'var(--alarm, #c13a2c)' }}>
                    A webhook was REFUSED{' '}
                    {saved.last_webhook_refused_at &&
                      `at ${new Date(saved.last_webhook_refused_at).toLocaleString()} `}
                    — {saved.last_webhook_refusal || 'signature did not verify'}
                    {saved.last_webhook_at && (
                      <>
                        {' '}(one verified successfully at{' '}
                        {new Date(saved.last_webhook_at).toLocaleString()}, so
                        this is a single bad request rather than a broken
                        connection.)
                      </>
                    )}
                  </b>
                ) : saved.last_webhook_at ? (
                  <>Last webhook verified{' '}
                    {new Date(saved.last_webhook_at).toLocaleString()}.</>
                ) : (
                  <>The secret is stored, but no webhook has arrived yet. If
                    texts are going out and receipts are not coming back, the
                    URL above is not registered or cannot be reached.</>
                )}
              </p>
            )}

            {c.fields.filter((f) => !f.group).map(renderField)}

            {/* THE ONE DELIBERATE EXCEPTION TO "EVERY CUSTOMER IS
                SEPARATELY WIRED". Google levels normally spend the
                platform's keys, so these are collapsed -- open only when
                this customer already has one of his own, which is the
                moment it matters that somebody can see it. */}
            {own.length > 0 && (
              <details open={!!(saved.has_api_key || saved.has_browser_key)}
                       style={{ margin: '4px 0 14px' }}>
                <summary className="dim" style={{ cursor: 'pointer', marginBottom: 10 }}>
                  Use this customer’s own Google keys
                </summary>
                <p className="blurb" style={{ marginTop: 0 }}>
                  For a customer with his own Google account, who pays
                  Google himself. A key here is used for this customer
                  INSTEAD of the platform’s; blank uses the platform’s
                  (server key {platform.has_server_key ? 'set' : 'not set'},
                  browser key {platform.has_browser_key ? 'set' : 'not set'}).
                  Restrict the server key to 216.128.176.101 and the
                  Geocoding and Places APIs, and the browser key to
                  https://*.crm.advancetalk.ca/* and the Maps JavaScript API.
                </p>
                {own.map(renderField)}
              </details>
            )}

            <label className="check">
              <input
                type="checkbox"
                checked={draft.enabled !== undefined ? draft.enabled === 'true' : !!saved.enabled}
                onChange={(e) =>
                  setDrafts({
                    ...drafts,
                    [c.kind]: { ...draft, enabled: String(e.target.checked) },
                  })
                }
              />
              Enabled
            </label>

            <div className="btnrow">
              <button className="primary compact" onClick={() => save(c.kind)}>
                Save
              </button>
              <button className="ghost" onClick={() => test(c.kind)}>
                Test connection
              </button>
              {/* Only offered once there is something to let go of. Nothing is
                  more alarming than a Disconnect button on a card that was
                  never connected. */}
              {status?.configured && (
                <button className="ghost danger"
                        onClick={() => revoke(c.kind, c.title)}>
                  Disconnect
                </button>
              )}
              {result && (
                <span className={`testresult ${result.ok ? 'ok' : 'bad'}`}>
                  {result.detail}
                </span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const PILL: Record<string, string> = {
  live: 'connected',
  off: 'switched off',
  // Credentials are right and one required setting is missing. Distinct from
  // "not working", which means we tried and it failed.
  blocked: 'one setting missing',
  error: 'not working',
  unconfigured: 'not set up',
}

function StatusPill({ status }: { status?: ConnectionStatus }) {
  const state = status?.state ?? 'unconfigured'
  return <span className={`pill ${state}`}>{PILL[state]}</span>
}

/** The line that answers "is this customer wired up right now" without
 *  pressing anything. That question was previously answerable only by pressing
 *  Test, and the answer disappeared on the next page load. */
function StatusLine({
  kind, status, saved, seats, listening,
}: {
  kind: string
  status?: ConnectionStatus
  saved: Record<string, any>
  seats?: { total: number; provisioned: number; ready: number }
  /** Whether the AMI listener is connected to the PBX at this moment. Read from
   *  the running process, not from a stored timestamp — a listener stuck in
   *  reconnect backoff has a healthy task and is watching nothing. */
  listening?: boolean
}) {
  if (!status) return null

  const when = status.checked_at
    ? new Date(status.checked_at).toLocaleString([], {
        dateStyle: 'medium', timeStyle: 'short',
      })
    : null

  const parts: string[] = []

  if (status.state === 'unconfigured') {
    parts.push('Nothing stored for this customer yet.')
  } else if (!status.enabled) {
    parts.push('Credentials are stored, but the connection is switched off.')
  } else if (status.state === 'blocked') {
    parts.push('Credentials are stored and the connection is on.')
  }

  // "Last confirmed working" is only said when nothing contradicts it. Saying
  // it beside a red pill and an error message is worse than saying nothing —
  // the reassuring half is the half in plain English, and it is the half people
  // read.
  const healthy = status.ok !== false && !status.detail
  if (when) {
    parts.push(healthy ? `Last confirmed working ${when}.` : `Last checked ${when}.`)
  } else if (status.configured && status.enabled) {
    parts.push('Never tested.')
  }

  if (status.detail) parts.push(status.detail)

  // The dispatch card is the only one where correct credentials are half the
  // answer: a customer can authenticate perfectly and still have no seat the
  // panel will open.
  if (kind === 'dispatch' && seats) {
    parts.push(
      seats.total === 0
        ? 'No seats defined.'
        : `${seats.ready} of ${seats.total} seat(s) can reach the radio` +
          (seats.provisioned > seats.ready
            ? `; ${seats.provisioned - seats.ready} provisioned but waiting on a fleet account.`
            : '.'),
    )
    if (saved.max_connections) {
      parts.push(`Licensed for ${saved.max_connections}.`)
    }
  }

  if (kind === 'pbx') {
    parts.push(
      listening
        ? `Connected and listening right now, to tenant ${saved.tenant_prefix} only.`
        : status.state === 'live'
          ? 'Not connected to the PBX at this moment.'
          : '',
    )
  }

  // WHOSE GOOGLE KEY this customer is spending, as the server decided it
  // (`geocoding/keys.py`) -- never worked out here, where a second copy of
  // "own wins" could say the platform's while requests spend his own. No
  // server key at all is the blocker's sentence, already above.
  if (kind === 'geocoder' && saved.map_level && saved.map_level !== 'osm') {
    if (saved.server_key_source) {
      parts.push(`Google requests use ${WHOSE[saved.server_key_source]} server key.`)
    }
    if (saved.map_level === 'google') {
      parts.push(
        saved.browser_key_source
          ? `Maps on screen load ${WHOSE[saved.browser_key_source]} browser key.`
          : 'No Google browser key is set anywhere, so the maps on screen fall ' +
            'back to OpenStreetMap.',
      )
    }
  }

  const text = parts.filter(Boolean).join(' ')
  if (!text) return null
  return <p className={`connstatus ${status.state}`}>{text}</p>
}
